/**
 * Tab HORARIO del detalle de cohorte (F3-A): editor de franjas semanales con
 * pre-flight de conflictos (aula/profesor), botón de generar sesiones en bloque
 * y lista de sesiones. Escritura local-first (write-path valida de nuevo el
 * conflicto de forma DURA); la generación va por REST.
 */
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useNavigate, useParams } from "@tanstack/solid-router";
import { ListChecks, Plus, Trash2, Video } from "lucide-solid";
import {
  createSlotMutation,
  deleteSlotMutation,
  handleApiResponse,
  joinWindowState,
  localDateKey,
  restoreTrashedMutation,
} from "@space/shared";
import { useUndo } from "@/lib/undo";
import { api } from "@/lib/api";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { waitForUploadQueueDrain } from "@/lib/powersync/wait-upload";
import { useCohortSessions, useCohortSlots, useRoomsByOrg } from "../hooks/use-schedule-data";
// F3-G: clases en vivo — badge/entrar/grabación + ajustes de proveedor.
import { EnVivoBadge } from "@/features/live/components/en-vivo-badge";
import { MeetingSettings } from "@/features/live/components/meeting-settings";
import { RecordingAttach } from "@/features/live/components/recording-attach";
import { RecordingPlayer } from "@/features/live/components/recording-player";
import { SessionMeetingButton } from "@/features/live/components/session-meeting-button";
import { liveApi } from "@/features/live/lib/live-api";
import { useNow } from "@/features/live/hooks/use-now";
import { useCohort } from "@/features/live/hooks/use-live-data";
// M5: editor del plan de clase (aula invertida), gateado por feature.
import { SessionPlanEditor } from "./session-plan-editor";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";

const field =
  "w-full rounded-lg border-2 border-border bg-field-background px-3 py-2 text-sm focus-visible:border-primary focus-visible:outline-none";

const WEEKDAYS: { value: number; label: MessageDescriptor }[] = [
  { value: 1, label: msg`Monday` },
  { value: 2, label: msg`Tuesday` },
  { value: 3, label: msg`Wednesday` },
  { value: 4, label: msg`Thursday` },
  { value: 5, label: msg`Friday` },
  { value: 6, label: msg`Saturday` },
  { value: 0, label: msg`Sunday` },
];

export const CohortScheduleTab: Component<{
  cohortId: string;
  organizationId: string;
  campusId: string | null;
}> = (props) => {
  const { t } = useI18n();
  const drizzle = useDrizzle();
  const powerSync = usePowerSyncContext();
  const { registerUndo } = useUndo();
  const navigate = useNavigate();
  const routeParams = useParams({ from: "/u/$u" });
  const now = useNow();
  const slots = useCohortSlots(() => props.cohortId);
  const sessions = useCohortSessions(() => props.cohortId);
  const rooms = useRoomsByOrg(() => props.organizationId);
  const cohort = useCohort(() => props.cohortId);

  // F3-G: default de proveedor en vivo de la cohorte (cohort.config.media_settings).
  const cohortMediaSettings = createMemo<Record<string, unknown>>(() => {
    const config = cohort.data?.[0]?.config;
    let parsed: Record<string, unknown> = {};
    if (typeof config === "string") {
      try {
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        parsed = JSON.parse(config) as Record<string, unknown>;
      } catch {
        parsed = {};
      }
    } else if (config && typeof config === "object") {
      parsed = config as Record<string, unknown>;
    }
    const ms = (parsed as { media_settings?: Record<string, unknown> }).media_settings;
    return ms ?? {};
  });
  const initialProvider = () =>
    (cohortMediaSettings() as { meetingProvider?: string }).meetingProvider ?? null;
  const initialConfig = (): Record<string, unknown> =>
    (cohortMediaSettings() as { meetingProviderConfig?: Record<string, unknown> })
      .meetingProviderConfig ?? {};

  const enterLive = (sessionId: string) => {
    void navigate({
      to: "/u/$u/live/$sessionId",
      params: { u: routeParams().u, sessionId },
    });
  };

  // M5: plan de clase (aula invertida) — feature + editor abierto por sesión.
  const flipped = useFeatureEnabled("academy.flipped_sessions");
  const courseId = () => cohort.data?.[0]?.course_id ?? undefined;
  const [planOpen, setPlanOpen] = createSignal<string | null>(null);

  const [weekday, setWeekday] = createSignal(1);
  const [start, setStart] = createSignal("18:00");
  const [end, setEnd] = createSignal("20:00");
  const [roomId, setRoomId] = createSignal("");
  const [conflict, setConflict] = createSignal<string | null>(null);

  // Fechas LOCALES: con la fecha UTC, en UTC-4 a partir de las 20:00 el rango
  // arrancaba "mañana" y la sesión de HOY jamás se generaba (COV).
  const [from, setFrom] = createSignal(localDateKey());
  const [to, setTo] = createSignal(localDateKey(new Date(Date.now() + 60 * 86_400_000)));

  const roomName = (id: string | null) =>
    id ? ((rooms.data ?? []).find((r) => r.id === id)?.name ?? id) : "—";

  const weekdayLabel = (w: number | null) =>
    t(WEEKDAYS.find((d) => d.value === w)?.label ?? msg`Day`);

  const addSlot = async () => {
    const db = drizzle();
    if (!db) {
      return;
    }
    setConflict(null);
    // Pre-flight de conflictos (aula doble-reservada / solape de profesor).
    try {
      const res = await handleApiResponse(
        api.POST("/api/schedule/validate", {
          body: {
            cohort_id: props.cohortId,
            kind: "slot",
            weekday: weekday(),
            start_time: start(),
            end_time: end(),
            room_id: roomId() ? roomId() : null,
          },
        }),
      );
      if (res && !res.ok) {
        setConflict(t(msg`Schedule conflict: room or teacher already booked.`));
        return;
      }
    } catch {
      // Si el pre-flight falla, el write-path sigue validando de forma dura.
    }
    await createSlotMutation(
      db,
      { cohortId: props.cohortId, organizationId: props.organizationId, campusId: props.campusId },
      {
        weekday: weekday(),
        startTime: start(),
        endTime: end(),
        roomId: roomId() ? roomId() : null,
      },
    );
  };

  const removeSlot = (id: string) => {
    const db = drizzle();
    if (!db) {
      return;
    }
    // F4-A: soft-delete + toast "Deshacer" (restaurable también en /trash).
    void (async () => {
      await deleteSlotMutation(db, id);
      registerUndo(t(msg`Schedule slot moved to trash`), async () => {
        const d = drizzle();
        if (d) {
          await restoreTrashedMutation(d, "schedule_slot", id);
        }
      });
    })();
  };

  const generate = async () => {
    try {
      // Costura local-first→REST: las franjas recién añadidas viajan por la
      // cola de subida de PowerSync; el generador LEE schedule_slot en el
      // server. Drenamos la cola para no generar contra un horario vacío.
      const ps = powerSync.db();
      if (ps) {
        await waitForUploadQueueDrain(ps);
      }
      const res = await handleApiResponse(
        api.POST("/api/cohorts/{id}/generate-sessions", {
          params: { path: { id: props.cohortId } },
          body: { from: from(), to: to() },
        }),
      );
      const created = res?.created ?? 0;
      const skipped = res?.skipped ?? 0;
      showToast(t(msg`Generated ${created} sessions (${skipped} already existed)`), "success");
    } catch {
      showToast(t(msg`Could not generate sessions`), "error");
    }
  };

  const sortedSessions = createMemo(() =>
    (sessions.data ?? []).toSorted((a, b) => (a.date ?? "").localeCompare(b.date ?? "")),
  );

  return (
    <div class="space-y-5" data-testid="cohort-schedule">
      {/* Editor de franjas */}
      <section class="space-y-3 rounded-2xl border border-border p-4">
        <h3 class="text-sm font-bold">{t(msg`Weekly slots`)}</h3>
        <div class="grid grid-cols-2 gap-2 md:grid-cols-5">
          <select
            class={field}
            data-testid="slot-weekday"
            value={weekday()}
            onChange={(e) => setWeekday(Number(e.currentTarget.value))}
          >
            <For each={WEEKDAYS}>{(d) => <option value={d.value}>{t(d.label)}</option>}</For>
          </select>
          <input
            type="time"
            class={field}
            data-testid="slot-start"
            value={start()}
            onInput={(e) => setStart(e.currentTarget.value)}
          />
          <input
            type="time"
            class={field}
            data-testid="slot-end"
            value={end()}
            onInput={(e) => setEnd(e.currentTarget.value)}
          />
          <select
            class={field}
            data-testid="slot-room"
            value={roomId()}
            onChange={(e) => setRoomId(e.currentTarget.value)}
          >
            <option value="">{t(msg`No room`)}</option>
            <For each={rooms.data ?? []}>{(r) => <option value={r.id}>{r.name}</option>}</For>
          </select>
          <Button data-testid="slot-add" onClick={() => void addSlot()} class="gap-1">
            <Plus class="h-4 w-4" /> {t(msg`Add`)}
          </Button>
        </div>
        <Show when={conflict()}>
          <p class="text-xs font-semibold text-danger" data-testid="slot-conflict">
            {conflict()}
          </p>
        </Show>
        <Show
          when={(slots.data ?? []).length > 0}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`No slots yet.`)}</p>}
        >
          <ul class="space-y-1">
            <For each={slots.data ?? []}>
              {(s) => (
                <li
                  class="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm"
                  data-testid="slot-row"
                >
                  <span>
                    <strong>{weekdayLabel(s.weekday)}</strong> {(s.start_time ?? "").slice(0, 5)}–
                    {(s.end_time ?? "").slice(0, 5)} · {roomName(s.room_id)}
                  </span>
                  <button
                    type="button"
                    data-testid="slot-delete"
                    onClick={() => removeSlot(s.id)}
                    class="text-muted-foreground hover:text-danger"
                    aria-label={t(msg`Delete slot`)}
                  >
                    <Trash2 class="h-4 w-4" />
                  </button>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </section>

      {/* Generación en bloque */}
      <section class="space-y-3 rounded-2xl border border-border p-4">
        <h3 class="text-sm font-bold">{t(msg`Generate sessions`)}</h3>
        <div class="flex flex-wrap items-end gap-2">
          <label class="space-y-1 text-xs text-muted-foreground">
            {t(msg`From`)}
            <input
              type="date"
              class={field}
              data-testid="generate-from"
              value={from()}
              onInput={(e) => setFrom(e.currentTarget.value)}
            />
          </label>
          <label class="space-y-1 text-xs text-muted-foreground">
            {t(msg`To`)}
            <input
              type="date"
              class={field}
              data-testid="generate-to"
              value={to()}
              onInput={(e) => setTo(e.currentTarget.value)}
            />
          </label>
          <Button variant="outline" data-testid="generate-sessions" onClick={() => void generate()}>
            {t(msg`Generate`)}
          </Button>
        </div>
      </section>

      {/* F3-G: proveedor de clase en vivo por defecto de la cohorte */}
      <MeetingSettings
        title={t(msg`Live class provider`)}
        description={t(
          msg`Default provider for this cohort's live sessions. Each session can override it.`,
        )}
        initialProvider={initialProvider()}
        initialConfig={initialConfig()}
        onSave={(provider, config) => liveApi.setCohortDefaults(props.cohortId, provider, config)}
      />

      {/* Lista de sesiones */}
      <section class="space-y-2">
        <h3 class="text-sm font-bold">{t(msg`Sessions`)}</h3>
        <Show
          when={sortedSessions().length > 0}
          fallback={<p class="text-sm text-muted-foreground">{t(msg`No sessions yet.`)}</p>}
        >
          <ul class="space-y-1">
            <For each={sortedSessions()}>
              {(s) => {
                const times = () => ({
                  date: s.date,
                  startTime: s.start_time,
                  endTime: s.end_time,
                });
                const isLive = () => joinWindowState(now(), times()).isLiveNow;
                return (
                  <li
                    class="space-y-2 rounded-lg border border-border px-3 py-2 text-sm"
                    data-testid="session-row"
                    data-session-status={s.status ?? ""}
                  >
                    <div class="flex flex-wrap items-center justify-between gap-2">
                      <span class="flex items-center gap-2">
                        {s.date} · {(s.start_time ?? "").slice(0, 5)}–
                        {(s.end_time ?? "").slice(0, 5)} · {roomName(s.room_id)}
                        <EnVivoBadge times={times()} />
                      </span>
                      <span class="flex flex-wrap items-center gap-2">
                        {/* M5: abrir/cerrar el plan de clase (aula invertida) */}
                        <Show when={flipped().enabled}>
                          <button
                            type="button"
                            class="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-semibold hover:bg-muted"
                            data-testid="session-plan-toggle"
                            aria-expanded={planOpen() === s.id}
                            onClick={() => setPlanOpen(planOpen() === s.id ? null : s.id)}
                          >
                            <ListChecks class="h-3.5 w-3.5" /> {t(msg`Plan`)}
                          </button>
                        </Show>
                        {/* Entrar (profesor) cuando está en vivo */}
                        <Show when={isLive()}>
                          <button
                            type="button"
                            class="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground"
                            data-testid="session-enter-live"
                            onClick={() => enterLive(s.id)}
                          >
                            <Video class="h-3.5 w-3.5" /> {t(msg`Enter`)}
                          </button>
                        </Show>
                        {/* Grabación: ver si existe, o subir */}
                        <Show
                          when={s.recording_video_id}
                          fallback={
                            <RecordingAttach sessionId={s.id} title={s.topic ?? undefined} />
                          }
                        >
                          <RecordingPlayer
                            videoId={s.recording_video_id!}
                            title={s.topic ?? undefined}
                          />
                        </Show>
                        <SessionMeetingButton
                          sessionId={s.id}
                          initialProvider={s.meeting_provider}
                        />
                        <span class="rounded-full bg-accent/12 px-2 py-0.5 text-xs font-bold text-accent">
                          {s.status}
                        </span>
                      </span>
                    </div>
                    {/* M5: editor del plan de clase de esta sesión */}
                    <Show when={flipped().enabled && planOpen() === s.id && courseId()}>
                      <SessionPlanEditor
                        sessionId={s.id}
                        cohortId={props.cohortId}
                        organizationId={props.organizationId}
                        campusId={props.campusId}
                        courseId={courseId()!}
                      />
                    </Show>
                  </li>
                );
              }}
            </For>
          </ul>
        </Show>
      </section>
    </div>
  );
};
